import { useState, useMemo, useCallback, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import dayjs, { Dayjs } from 'dayjs'
import { CheckOutlined, InfoCircleFilled } from '@ant-design/icons'
import {
  Button,
  PageHeader,
  Table,
  RangePicker,
  GroupedUsersMultiSelect,
  GroupedResidentsMultiSelect,
  MultiSelect,
  FilterContainer
} from '@lmc/ui-kit'
import { RotaDrawer } from '../RotaDrawer/RotaDrawer'
import type { SorterResult } from 'antd/es/table/interface'
import { useQueryClient } from 'react-query'
import { showToast } from 'common/utils'
import useUsersList from 'screens/Rostering/hooks/useUsersList'
import useResidentsList from 'screens/Rostering/hooks/useResidentsList'
import { useShiftTypesList } from 'screens/Rostering/hooks/useShiftTypesList'
import { useVisitTypesList } from 'screens/Rostering/hooks/useVisitTypesList'
import type {
  ReconciliationRow,
  ReconciliationFilters,
  ReconcilePayload,
  ReconciliationSortField,
  DurationDiscrepancyApi
} from '../../Reports/types/reconciliation'
import { ReconciliationPayType } from '../../Reports/types/reconciliation'
import {
  useGetReconciliation,
  useReconcileOne,
  useBulkReconcile,
  RECONCILIATION_QUERY_KEY
} from '../../Reports/hooks/useReconciliation'
import { ReconcilePaidTimeModal } from './ReconciliationModalV2/ReconcilePaidTimeModal'
import { RECONCILIATION_MODAL_DEV_FALLBACK_ROW } from './ReconciliationModalV2/reconcileModalDevFallback'
import {
  getReconciliationRangeDisabledDate,
  isValidReconciliationDateRange,
  RECONCILIATION_MAX_PAST_MONTHS,
  RECONCILIATION_DURATION_DISCREPANCY_UI_TO_API,
  mapReconciliationApiItemToRow,
  rowKey
} from './reconciliationUtils'
import { getReconciliationTableColumns } from './reconciliationTableColumns'
import { DontPayModal, CustomAmountModal } from './ReconciliationModals'

import { Spinner } from 'components/generic'

const RECONCILIATION_PAGE_SIZE = 10

const ReconciliationCustomCellWrapper = ({
  children,
  ...props
}: {
  children: ReactNode
}) => <ReconciliationCustomCell {...props}>{children}</ReconciliationCustomCell>

const ReconciliationCustomTableWrapper = ({
  children,
  ...props
}: {
  children: ReactNode
}) => (
  <ReconciliationCustomTable {...props}>{children}</ReconciliationCustomTable>
)

const reconciliationTableComponents = {
  table: ReconciliationCustomTableWrapper,
  body: {
    cell: ReconciliationCustomCellWrapper
  }
}

export const ReconciliationPage = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const statusOptions = useMemo(
    () => [
      {
        label: t('RECONCILIATION_STATUS_NEEDS_ATTENTION'),
        value: 'needs_attention'
      },
      { label: t('RECONCILIATION_STATUS_RECONCILED'), value: 'reconciled' }
    ],
    [t]
  )

  const discrepancyOptions = useMemo(
    () => [
      { label: t('RECONCILIATION_DISCREPANCY_LT5'), value: 'lt5' },
      { label: t('RECONCILIATION_DISCREPANCY_5TO15'), value: '5to15' },
      { label: t('RECONCILIATION_DISCREPANCY_15TO30'), value: '15to30' },
      { label: t('RECONCILIATION_DISCREPANCY_GT30'), value: 'gt30' }
    ],
    [t]
  )

  const bulkPayTypeOptions = useMemo(
    () => [
      {
        label: t('RECONCILIATION_PAY_BY_PLANNED'),
        value: ReconciliationPayType.PLANNED
      },
      {
        label: t('RECONCILIATION_PAY_BY_ACTUAL'),
        value: ReconciliationPayType.ACTUAL
      },
      { label: t('RECONCILIATION_NO_PAY'), value: ReconciliationPayType.NONE }
    ],
    [t]
  )

  const defaultStart = dayjs().subtract(7, 'day')
  const defaultEnd = dayjs().subtract(1, 'day')

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    defaultStart,
    defaultEnd
  ])
  const [statusFilter, setStatusFilter] = useState<string[]>([])
  const [discrepancyFilter, setDiscrepancyFilter] = useState<string[]>([])
  const [teamMemberFilter, setTeamMemberFilter] = useState<string[]>([])
  const [serviceUserFilter, setServiceUserFilter] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState<string | undefined>('date')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>('desc')

  const [eventTypeFilter, setEventTypeFilter] = useState<string[]>([])

  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const [bulkPayType, setBulkPayType] = useState<ReconciliationPayType>(
    ReconciliationPayType.PLANNED
  )
  const [reconcileRow, setReconcileRow] = useState<ReconciliationRow | null>(
    null
  )
  const [drawerInstanceId, setDrawerInstanceId] = useState<string | null>(null)
  const [dontPayRow, setDontPayRow] = useState<ReconciliationRow | null>(null)
  const [customAmountRow, setCustomAmountRow] = useState<ReconciliationRow | null>(null)

  const { users } = useUsersList({ active: true })
  const { residents } = useResidentsList({})

  const { shiftTypes } = useShiftTypesList()
  const { visitTypes } = useVisitTypesList()

  const eventTypeOptions = useMemo(() => {
    const shifts = (shiftTypes ?? []).map((st: any) => ({
      label: st.name,
      value: st.id ?? st._id
    }))
    const visits = (visitTypes ?? []).map((vt: any) => ({
      label: vt.name,
      value: vt.id ?? vt._id
    }))
    return [...shifts, ...visits]
  }, [shiftTypes, visitTypes])

  const apiSortField = useMemo((): ReconciliationSortField | undefined => {
    if (!sortBy || !sortOrder) return undefined
    const m: Record<string, ReconciliationSortField> = {
      date: 'startTime',
      status: 'status',
      teamMember: 'teamMemberName',
      eventType: 'instanceTypeId',
      plannedTime: 'plannedDuration'
    }
    return m[sortBy]
  }, [sortBy, sortOrder])

  const filters: ReconciliationFilters = {
    startTime: dateRange[0].startOf('day').toISOString(),
    endTime: dateRange[1].endOf('day').toISOString(),
    ...(statusFilter.length ? { status: statusFilter } : {}),
    ...(discrepancyFilter.length
      ? {
          durationDiscrepancy: discrepancyFilter
            .map((d) => RECONCILIATION_DURATION_DISCREPANCY_UI_TO_API[d])
            .filter((x): x is DurationDiscrepancyApi => Boolean(x))
        }
      : {}),
    ...(teamMemberFilter.length ? { teamMemberIds: teamMemberFilter } : {}),
    ...(serviceUserFilter.length ? { serviceUserIds: serviceUserFilter } : {}),
    ...(eventTypeFilter.length ? { eventTypeIds: eventTypeFilter } : {}),
    ...(apiSortField !== undefined && sortOrder
      ? {
          sortField: apiSortField,
          sortDir: sortOrder === 'asc' ? (1 as const) : (-1 as const)
        }
      : {}),
    page,
    pageSize: RECONCILIATION_PAGE_SIZE
  }

  const { data, isLoading } = useGetReconciliation(filters)

  const rows = useMemo(() => {
    if (!data?.data?.length) return []
    return data.data.map(mapReconciliationApiItemToRow)
  }, [data])
  const { mutate: reconcileOne, isLoading: isReconciling } = useReconcileOne()
  const { mutate: bulkReconcile, isLoading: isBulkReconciling } =
    useBulkReconcile()

  const total = data?.pagination?.totalItems ?? 0

  const handleRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates?.[0] || !dates?.[1]) return
    if (!isValidReconciliationDateRange(dates[0], dates[1])) return
    setDateRange([dates[0], dates[1]])
    setPage(1)
    setSelectedRowKeys([])
  }

  const handleTableChange = (
    _pagination: unknown,
    _filters: unknown,
    sorter: SorterResult<ReconciliationRow> | SorterResult<ReconciliationRow>[],
    extra: { action: 'paginate' | 'sort' | 'filter' }
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter
    if (s?.columnKey && s.order) {
      setSortBy(String(s.columnKey))
      setSortOrder(s.order === 'ascend' ? 'asc' : 'desc')
    } else {
      setSortBy(undefined)
      setSortOrder(undefined)
    }
    // Only reset to page 1 for sort/filter; pagination changes are handled by pagination.onChange
    if (extra?.action !== 'paginate') {
      setPage(1)
      setSelectedRowKeys([])
    }
  }

  const handleReconcilePaidTimeSave = (payload: ReconcilePayload) => {
    const target = reconcileRow
    if (!target?.rotaInstanceId) {
      showToast({
        type: 'warning',
        title: t('RECONCILIATION_RECONCILE_NEED_ROW')
      })
      return
    }
    reconcileOne(
      {
        rotaInstanceId: target.rotaInstanceId,
        teamMemberId: target.teamMemberId,
        payload
      },
      {
        onSuccess: () => {
          setReconcileRow(null)
          queryClient.invalidateQueries([RECONCILIATION_QUERY_KEY])
        }
      }
    )
  }

  const handleBulkReconcile = () => {
    if (selectedRowKeys.length === 0) return
    const selectedRows = rows.filter(
      (r) =>
        selectedRowKeys.includes(rowKey(r)) &&
        !r.isCanceled &&
        Boolean(r.rotaInstanceId)
    )
    if (selectedRows.length === 0) return

    submitBulkReconcile(selectedRows)
  }

  const submitBulkReconcile = (selectedRows: ReconciliationRow[]) => {
    bulkReconcile(
      {
        items: selectedRows.map((r) => ({
          rotaInstanceId: r.rotaInstanceId,
          teamMemberId: r.teamMemberId
        })),
        payType: bulkPayType
      },
      {
        onSuccess: (result: any) => {
          const updated = result?.updated ?? 0
          const skipped = result?.skipped ?? 0
          if (updated === 0 && skipped > 0) {
            showToast({
              type: 'warning',
              title:
                skipped === 1
                  ? t('RECONCILIATION_TOAST_SKIPPED_ONE')
                  : t('RECONCILIATION_TOAST_SKIPPED_MANY', { count: skipped })
            })
          } else if (skipped > 0) {
            showToast({
              type: 'warning',
              title: t('RECONCILIATION_TOAST_PARTIAL', { updated, skipped })
            })
          } else {
            showToast({
              type: 'success',
              title:
                updated === 1
                  ? t('RECONCILIATION_TOAST_SUCCESS_ONE')
                  : t('RECONCILIATION_TOAST_SUCCESS_MANY', { count: updated })
            })
          }
          setSelectedRowKeys([])
          queryClient.invalidateQueries([RECONCILIATION_QUERY_KEY])
        }
      }
    )
  }

  const handleReconcileClick = useCallback((record: ReconciliationRow) => {
    setDrawerInstanceId(null)
    setReconcileRow(record)
  }, [])

  const handleDontPayClick = useCallback((record: ReconciliationRow) => {
    setDontPayRow(record)
  }, [])

  const handleEditPayAmount = useCallback((record: ReconciliationRow) => {
    setCustomAmountRow(record)
  }, [])

  const columns = useMemo(
    () =>
      getReconciliationTableColumns({
        t,
        onDontPay: handleDontPayClick,
        onEditPayAmount: handleEditPayAmount
      }),
    [t, handleReconcileClick, handleDontPayClick, handleEditPayAmount]
  )

  return (
    <>
      <PageHeaderSection>
        <PageHeader
          title={t('RECONCILIATION')}
          subtitle={t('RECONCILIATION_INTRO')}
        />
      </PageHeaderSection>

      <PageBody>
        <FilterContainer title={t('FILTERS')}>
          <>
            <RangePicker
              value={dateRange}
              onChange={handleRangeChange}
              disabledDate={getReconciliationRangeDisabledDate}
              allowClear={false}
              format="DD-MM-YYYY"
              minDate={dayjs().subtract(
                RECONCILIATION_MAX_PAST_MONTHS,
                'month'
              )}
              maxDate={dayjs()}
            />
            <GroupedResidentsMultiSelect
              residents={residents}
              title={t('RECONCILIATION_TABLE_SERVICE_USER')}
              values={serviceUserFilter}
              handleInputChange={(selected) => {
                setServiceUserFilter(selected as string[])
                setPage(1)
                setSelectedRowKeys([])
              }}
            />
            <GroupedUsersMultiSelect
              users={users}
              title={t('TEAM_MEMBERS')}
              values={teamMemberFilter}
              handleInputChange={(selected) => {
                setTeamMemberFilter(selected as string[])
                setPage(1)
                setSelectedRowKeys([])
              }}
            />
            <NarrowFilterMultiSelect
              title={t('RECONCILIATION_TABLE_TYPE_OF_EVENT')}
              values={eventTypeFilter}
              options={eventTypeOptions}
              hasClear
              hasSearch
              onChange={(selected) => {
                setEventTypeFilter(selected as string[])
                setPage(1)
                setSelectedRowKeys([])
              }}
            />
            <NarrowFilterMultiSelect
              title={t('RECONCILIATION_TABLE_STATUS')}
              values={statusFilter}
              options={statusOptions}
              hasClear
              onChange={(selected) => {
                setStatusFilter(selected as string[])
                setPage(1)
                setSelectedRowKeys([])
              }}
            />
            <NarrowFilterMultiSelect
              title={t('RECONCILIATION_TABLE_DURATION_DISCREPANCY')}
              values={discrepancyFilter}
              options={discrepancyOptions}
              hasClear
              onChange={(selected) => {
                setDiscrepancyFilter(selected as string[])
                setPage(1)
                setSelectedRowKeys([])
              }}
            />
          </>
        </FilterContainer>

        {selectedRowKeys.length === 0 ? (
          <BulkInfoBar>
            <BulkInfoIcon />
            <BulkInfoText>{t('RECONCILIATION_SELECT_ROWS_INFO')}</BulkInfoText>
          </BulkInfoBar>
        ) : (
          <BulkActionBar>
            <BulkSelectionCount>
              {t('RECONCILIATION_N_SELECTED', {
                count: selectedRowKeys.length
              })}
            </BulkSelectionCount>
            <BulkToolbarDivider />
            <BulkRadioInner>
              {bulkPayTypeOptions.map((opt) => (
                <BulkRadioLabel key={opt.value}>
                  <BulkRadioInput
                    type="radio"
                    name="bulk-pay-type"
                    value={opt.value}
                    checked={bulkPayType === opt.value}
                    onChange={() =>
                      setBulkPayType(opt.value as ReconciliationPayType)
                    }
                  />
                  {opt.label}
                </BulkRadioLabel>
              ))}
            </BulkRadioInner>
            <BulkReconcileWrap>
              <Button
                variant="primary"
                onClick={handleBulkReconcile}
                isLoading={isBulkReconciling}
                icon={<CheckOutlined />}
              >
                {t('RECONCILIATION_RECONCILE')}
              </Button>
            </BulkReconcileWrap>
          </BulkActionBar>
        )}

        {isLoading ? (
          <TableLoaderWrap>
            <Spinner />
          </TableLoaderWrap>
        ) : (
          <ReconciliationTableScroll>
            <StyledTable
              components={reconciliationTableComponents}
              columns={columns}
              dataSource={rows}
              rowKey={rowKey}
              rowClassName={(_, index) =>
                index % 2 === 0 ? 'even-row' : 'odd-row'
              }
              onRow={(record) => ({
                onClick: (e) => {
                  const el = e.target as HTMLElement
                  if (
                    el.closest('.ant-table-selection-column') ||
                    el.closest('.ant-checkbox-wrapper') ||
                    el.closest('.ant-checkbox')
                  ) {
                    return
                  }
                  if (record.rotaInstanceId) {
                    setDrawerInstanceId(record.rotaInstanceId)
                  }
                },
                style: {
                  cursor: record.rotaInstanceId ? 'pointer' : 'default'
                }
              })}
              rowSelection={{
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys as string[]),
                getCheckboxProps: (record) => ({
                  disabled: record.isCanceled
                })
              }}
              pagination={{
                current: page,
                pageSize: RECONCILIATION_PAGE_SIZE,
                total,
                onChange: (p) => {
                  setPage(p)
                  setSelectedRowKeys([])
                },
                showSizeChanger: false,
                position: ['bottomCenter']
              }}
              onChange={handleTableChange}
              size="small"
              scroll={{ x: 'max-content' }}
            />
          </ReconciliationTableScroll>
        )}
      </PageBody>

      {drawerInstanceId && (
        <RotaDrawer
          isVisible={true}
          id={drawerInstanceId}
          onClose={() => setDrawerInstanceId(null)}
        />
      )}

      <ReconcilePaidTimeModal
        open={reconcileRow !== null}
        row={reconcileRow ?? RECONCILIATION_MODAL_DEV_FALLBACK_ROW}
        onClose={() => setReconcileRow(null)}
        onSave={handleReconcilePaidTimeSave}
        isLoading={isReconciling}
      />

      <DontPayModal
        open={dontPayRow !== null}
        row={dontPayRow}
        onClose={() => setDontPayRow(null)}
        onSave={(_row, _reason) => {
          setDontPayRow(null)
        }}
      />

      <CustomAmountModal
        open={customAmountRow !== null}
        row={customAmountRow}
        onClose={() => setCustomAmountRow(null)}
        onSave={(_row, _amount) => {
          setCustomAmountRow(null)
        }}
      />
    </>
  )
}

export default ReconciliationPage

const TableLoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  width: 100%;
  border-radius: 20px;
  background: ${({ theme }) => theme.color.neutral.color10};
`

const ReconciliationTableScroll = styled.div`
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  border-radius: 20px;
`

const StyledTable = styled(Table)`
  & .ant-table {
    min-width: max-content;
  }

  & .ant-table-thead > tr > th {
    white-space: nowrap;
  }

  & .ant-table-column-sorters {
    flex-direction: row-reverse;
    justify-content: flex-end;
  }

  & .ant-table-column-sorter {
    margin-right: 4px;
    margin-left: 0;
  }

  & .ant-table-thead > tr > th.reconciliation-header-multiline {
    white-space: pre-line;
    vertical-align: bottom;
  }

  & .even-row {
    background-color: ${({ theme }) => theme.color.neutral.color11};
  }

  & .odd-row {
    background-color: ${({ theme }) => theme.color.neutral.color10};
  }
` as typeof Table

const NarrowFilterMultiSelect = styled(MultiSelect)`
  min-width: 160px;
  width: 216px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
    max-width: none;
  }
` as typeof MultiSelect

const PageHeaderSection = styled.div`
  padding: 24px 48px 0;
`

const PageBody = styled.div`
  padding: 24px 48px 48px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const BulkToolbarSurface = styled.div`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-height: 56px;
  padding: 10px 16px;
  background: ${({ theme }) => theme.color.neutral.color10};
  border: 1px solid ${({ theme }) => theme.color.neutral.color9};
  border-radius: 8px;
`

const BulkInfoBar = styled(BulkToolbarSurface)`
  gap: 12px;
`

const BulkInfoIcon = styled(InfoCircleFilled)`
  color: ${({ theme }) => theme.color.status.primary};
  font-size: 14px;
  flex-shrink: 0;
`

const BulkInfoText = styled.span`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-size: 14px;
  line-height: 1.4;
`

const BulkActionBar = styled(BulkToolbarSurface)`
  flex-wrap: wrap;
  gap: 16px;
`

const BulkSelectionCount = styled.span`
  color: ${({ theme }) => theme.color.neutral.color2};
  font-weight: 500;
  flex-shrink: 0;
`

const BulkToolbarDivider = styled.div`
  width: 1px;
  align-self: stretch;
  min-height: 24px;
  background: ${({ theme }) => theme.color.neutral.color8};
  flex-shrink: 0;
`

const BulkReconcileWrap = styled.div`
  margin-left: auto;
  display: flex;
`

const BulkRadioInner = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const BulkRadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  width: auto;
  color: inherit;
  font-weight: normal;
`

const BulkRadioInput = styled.input`
  accent-color: #1677ff;
  cursor: pointer;
  margin: 0;
`

const ReconciliationCustomTable = styled.table`
  background: ${({ theme }) => theme.color.background.tertiary};
`

const ReconciliationCustomCell = styled.td`
  padding: 16px 12px !important;
  background-color: inherit !important;
`
